import { Component, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import {
    getAutocompleteUsers,
    getFilterColumns,
    isLoadingUsers
} from 'app/organization/core/store/selectors/filter-users.selector';
import { getSpecificUsersList } from 'app/organization/user-groups/store/selectors/current-user-groups.selector';
import {
    FilterUsers,
    FilterUsersSuccess,
    GetFilterColumns
} from 'app/organization/core/store/actions/filter-user.actions';
import { debounceTime } from 'rxjs/operators';
import { DELAY_FILTER_USERS } from 'app/core/models/constants';
import { CalendarsList, GetUserCalendar, SetBaseCalendarUser, SetSelectedUser } from '../store/actions/calendars.actions';
import { getBaseCalendarByUser, getBaseCalendarList, getSelectedUser } from '../store/selectors/calendars.selectors';
import { RequestPendingChanges, pendingChangesAction } from 'app/core/store/actions/pending-changes.actions';
import { selectPendingChangeAction } from 'app/core/store/selectors/pending-changes.selector';
import { EPendingChangesSelectedAction } from 'app/core/models/pending-changes.enum';
import { IAppState } from 'app/IAppState';


@Component({
    selector: 'working-time-schema-detail',
    templateUrl: 'working-time-schema-detail.component.html',
    styleUrls: ['working-time-schema-detail.component.scss']
})

export class WorkingTimeSchemaDetailComponent {
    @ViewChild('autocompleteUser', { static: false}) autocompleteUser;
    public valueToSearch: string;
    public selectedFilterColumn: string;
    public autocompleteUsers$: Observable<ISpecificUser[]>;
    public users$: Observable<Array<IUserGroupUser>>;
    public filterColumns$: Observable<string[]>;
    public isLoadingUsers$: Observable<boolean>;
    public selectedUser: ISpecificUser;
    public baseCalendars = [];
    public selectedBaseCalendar: number;
    public selectedUser$: Observable<ISpecificUser>;
    public nextSelectedUser: ISpecificUser;
    private _searchDebounce = new Subject();
    private _searchDebounceSuscription: Subscription;
    private _filterColumnsSubscription: Subscription;
    private _baseCalendarByUserSubscription: Subscription;
    public _baseCalendars: Subscription;
    private _pendingChangesActionSubscription: Subscription;
    private _selectedUserSubscription: Subscription;

    public filterColumnsResources = {
        ContactEmail: 'USER_GROUPS.CONTACT_EMAIL',
        FullName: 'USER_GROUPS.FULLNAME',
        UserName: 'USER_GROUPS.USERNAME'
    };

    constructor(private _store: Store<IAppState>) {
        this.filterColumns$ = this._store.pipe(select(getFilterColumns));
        this.autocompleteUsers$ = this._store.pipe(select(getAutocompleteUsers));
        this.isLoadingUsers$ = this._store.pipe(select(isLoadingUsers));
        this.users$ = this._store.pipe(select(getSpecificUsersList));
        this._baseCalendars = this._store.pipe(select(getBaseCalendarList)).subscribe(result => {
          this.baseCalendars = result;
        });
        this._baseCalendarByUserSubscription = this._store.pipe(select(getBaseCalendarByUser)).subscribe((calendar: IBaseCalendar) => {
            this.selectedBaseCalendar = calendar.idWorkingTimeSchema || -1;
        });
        this._selectedUserSubscription = this._store.pipe(select(getSelectedUser)).subscribe((user) => {
            this.selectedUser = user
        });
    }

    ngOnInit() {
        this._initialize();
        this._pendingChangesValidationAction();
    }

    filter(valueToSearch: string) {
        this.valueToSearch = valueToSearch;
        if (this.valueToSearch) {
            this._searchDebounce.next();
        } else {
            this._store.dispatch(new FilterUsersSuccess([]));
        }
    }

    userSelected(event: { value: ISpecificUser }) {
        if (!_.isEqual(this.nextSelectedUser, event.value)) {
            this.nextSelectedUser = event.value;
            this._store.dispatch(new pendingChangesAction(EPendingChangesSelectedAction.DONOTHING));
            this._store.dispatch(new RequestPendingChanges());
        }
    }

    displayFn(user?: ISpecificUser): string | undefined {
        return user ? user.fullName : undefined;
    }

    ngOnDestroy() {
        this._store.dispatch(new FilterUsersSuccess([]));
        if (this._searchDebounceSuscription) {
            this._searchDebounceSuscription.unsubscribe();
        }
        if (this._filterColumnsSubscription) {
            this._filterColumnsSubscription.unsubscribe();
        }
        if (this._baseCalendarByUserSubscription) {
            this._baseCalendarByUserSubscription.unsubscribe();
        }
        if (this._pendingChangesActionSubscription) {
            this._pendingChangesActionSubscription.unsubscribe();
        }
        if (this._selectedUserSubscription) {
            this._selectedUserSubscription.unsubscribe();
        }
    }

    onBaseCalendarChanged(calendar: IBaseCalendar) {
        this._store.dispatch(new SetBaseCalendarUser(calendar));
    }

    private _initialize() {
        this._store.dispatch(new GetFilterColumns);
        this._store.dispatch(new CalendarsList);
        this._searchDebounceSuscription = this._searchDebounce.pipe(debounceTime(DELAY_FILTER_USERS)).subscribe(() => {
            const valueToSearch = this.valueToSearch ? this.valueToSearch.trim() : this.valueToSearch;
            if (valueToSearch) {
                this._store.dispatch(new FilterUsers({ filterColumn: this.selectedFilterColumn, valueToSearch: this.valueToSearch }));
            }
        });
        this._filterColumnsSubscription = this.filterColumns$.subscribe((columns: string[]) => {
            this.selectedFilterColumn = columns.length > 0
                ? columns.find((column: string) => {
                    return column === 'FullName'
                })
                : '';
        });
    }

    private _pendingChangesValidationAction() {
        this._pendingChangesActionSubscription = this._store
          .pipe(select(selectPendingChangeAction))
          .subscribe((action: EPendingChangesSelectedAction) => {
            switch (action) {
              case EPendingChangesSelectedAction.CANCEL:
                this._store.dispatch(new SetSelectedUser(this.selectedUser));
                this._store.dispatch(
                  new GetUserCalendar(this.selectedUser.idUser)
                );
                this.nextSelectedUser = _.cloneDeep(this.selectedUser);
                this.autocompleteUser.input.nativeElement.value = this.selectedUser.fullName;
                break;

              case EPendingChangesSelectedAction.SAVE:
              case EPendingChangesSelectedAction.DONTSAVE:
              case EPendingChangesSelectedAction.CONTINUE:
                if (this.nextSelectedUser) {
                  this._store.dispatch(
                    new SetSelectedUser(this.nextSelectedUser)
                  );
                  this._store.dispatch(
                    new GetUserCalendar(this.nextSelectedUser.idUser)
                  );
                }
                break;
            }
          });
    }
}
